import actionType from '../actions/actionTypes';
import { QTYPES, QuestionSummary } from '@nexxt/common/types';
import {
    sortByIndexRecursive,
    sortQuestion,
    updateQuestionSummary,
    getFlowAfterQuestionCreation,
    parseQuestionSummaryToObjects,
    getFlowAfterQuestionRemoval
} from '../utils/question';
import { BUILDER_STATES } from '../interface/common.interface';

export interface IQuestionReducer {
    questionSummaries: {
        [projectId: number]: QuestionSummary[];
    };
    currentQuestionId: number;
    currentQuestion: any;
    previewPackageId: number;
    isNewQuestion: boolean;
    showError: boolean;
    builderState: BUILDER_STATES;
    isLogic: boolean;
    formRenderCount: number; // to force re-render for new question form
}

const initialState: IQuestionReducer = {
    questionSummaries: {},
    currentQuestionId: null,
    currentQuestion: {},
    previewPackageId: null,
    isNewQuestion: false,
    showError: false,
    builderState: BUILDER_STATES.EDIT,
    isLogic: false,
    formRenderCount: 0
};

export default function (
    state: IQuestionReducer = initialState,
    action
): IQuestionReducer {
    switch (action.type) {
        /**
         * Fetched by GET flow API
         * Get all question flow
         * save question flow in questionTree
         */
        case actionType.GET_ALL_QUESTIONS_FLOW: {
            return {
                ...state,

                questionSummaries: {
                    ...state.questionSummaries,
                    [action.payload.projectId]: action.payload.questions
                }
            };
        }

        /**
         * Called in sortQuestions in question.service.ts
         * Prompt when user dnd a question in question list in the first level of depth(parent = 0)
         */
        case actionType.SORT_QUESTIONS: {
            const { dragIndex, hoverIndex } = action.payload;
            return {
                ...state,

                questionSummaries: {
                    ...state.questionSummaries,
                    [action.payload.projectId]: sortQuestion(
                        state.questionSummaries[action.payload.projectId],
                        dragIndex,
                        hoverIndex,
                        0
                    )
                }
            };
        }
        /**
         * Called in sortQuestions in question.service.ts
         * Prompt when user dnd a question in question group
         */
        case actionType.SORT_QUESTIONS_BY_GROUP: {
            return {
                ...state,

                questionSummaries: {
                    ...state.questionSummaries,
                    [action.payload.projectId]: sortByIndexRecursive(
                        parseQuestionSummaryToObjects(
                            state.questionSummaries[action.payload.projectId]
                        ),
                        action.payload.questionId,
                        [...state.questionSummaries[action.payload.projectId]],
                        action.payload.dragIndex,
                        action.payload.hoverIndex,
                        action.payload.questionId
                    )
                }
            };
        }
        /**
         * Save/update sorted question list.
         * Called in saveUpdatedSortedQuestion in question.service.ts
         */
        case actionType.AFTER_SAVE_SORT_QUESTIONS: {
            return {
                ...state,

                questionSummaries: {
                    ...state.questionSummaries,
                    [action.payload.projectId]: action.payload.questions
                }
            };
        }
        case actionType.EDIT_GREETING:
            return {
                ...state,
                builderState: BUILDER_STATES.EDIT,
                isNewQuestion: false,
                isLogic: false,
                currentQuestion: { type: action.payload.questionType }
            };

        /**
         * Set new question type from question builder
         *  */

        case actionType.SET_ADD_NEW_QUESTION: {
            const { questionType, position, parent } = action.payload;
            return {
                ...state,
                isNewQuestion: true,
                showError: false,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: null,
                currentQuestion: { type: questionType, position, parent },
                formRenderCount: state.formRenderCount + 1
            };
        }

        /**
         * call quetion details and save in currentQuestion/currentQuestionId
         */
        case actionType.EDIT_QUESTION:
        case actionType.SET_EDIT_QUESTION: {
            return {
                ...state,
                currentQuestion: action.payload.question,
                isNewQuestion: false,
                showError: true,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: action.payload.questionId
            };
        }

        case actionType.SET_ACTIVE_QUESTION: {
            return {
                ...state,
                currentQuestion: action.payload.question,
                currentQuestionId: action.payload.questionId
            };
        }

        case actionType.PREVIEW_QUESTION: {
            return {
                ...state,
                previewPackageId: action.payload.previewPackageId,
                builderState: BUILDER_STATES.PREVIEW,
                isNewQuestion: false,
                isLogic: false
            };
        }

        case actionType.CREATE_QUESTION: {
            const { projectID, question, questionSummary } = action.payload;
            const currentQuestionSummary = state.questionSummaries[projectID];

            return {
                ...state,
                isNewQuestion: false,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: question.id,
                currentQuestion: question,

                questionSummaries: {
                    ...state.questionSummaries,
                    [projectID]: getFlowAfterQuestionCreation(
                        currentQuestionSummary,
                        questionSummary
                    )
                }
            };
        }
        case actionType.CREATE_QUESTIONS: {
            const { projectId, question, questionSummary } = action.payload;
            const currentQuestionSummary = state.questionSummaries[projectId];

            return {
                ...state,
                isNewQuestion: false,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: question.id,
                currentQuestion: question,

                questionSummaries: {
                    ...state.questionSummaries,
                    [projectId]: getFlowAfterQuestionCreation(
                        currentQuestionSummary,
                        questionSummary
                    )
                }
            };
        }

        case actionType.UPDATE_QUESTION: {
            const { projectID, question, questionSummary } = action.payload;
            let _questionSummaries: QuestionSummary[] = [
                ...state.questionSummaries[projectID]
            ];

            _questionSummaries = updateQuestionSummary(
                _questionSummaries,
                questionSummary
            );
            return {
                ...state,
                currentQuestion: question,
                questionSummaries: {
                    ...state.questionSummaries,
                    [projectID]: _questionSummaries
                }
            };
        }

        case actionType.DUPLICATE_QUESTION: {
            const { projectID, question, questionSummary } = action.payload;

            return {
                ...state,
                questionSummaries: {
                    ...state.questionSummaries,
                    [projectID]: [
                        ...state.questionSummaries[projectID],
                        questionSummary
                    ]
                },
                currentQuestion: question,
                isNewQuestion: false,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: question.id
            };
        }

        case actionType.DELETE_QUESTION: {
            const builderState = state.builderState;
            // let selectedQuestion = { ...state.selectedQuestion };
            const currentQuestion = { ...state.currentQuestion };
            const currentQuestionId = state.currentQuestionId;
            const projectID = action.payload.projectID;
            const questionID = action.payload.questionID;

            let questionSummaries = [...state.questionSummaries[projectID]];
            questionSummaries = getFlowAfterQuestionRemoval(
                questionSummaries,
                questionID
            );

            return {
                ...state,
                questionSummaries: {
                    ...state.questionSummaries,
                    [projectID]: questionSummaries
                },
                builderState,
                currentQuestion,
                currentQuestionId
            };
        }
        case actionType.SET_LOGIC_QUESTION: {
            if (action.payload.questionId) {
                return {
                    ...state,
                    currentQuestion:
                        action.payload.question || state.currentQuestion,
                    isNewQuestion: false,
                    builderState: BUILDER_STATES.LOGIC,
                    isLogic: true
                };
            } else {
                return {
                    ...state,
                    isNewQuestion: false,
                    builderState: BUILDER_STATES.LOGIC,
                    isLogic: true
                };
            }
        }
        case actionType.SET_SAMPLE: {
            return {
                ...state,
                currentQuestion: action.payload.question,
                isNewQuestion: false,
                builderState: BUILDER_STATES.EDIT,
                isLogic: false,
                currentQuestionId: action.payload.questionId
            };
        }

        case actionType.CLEAR_QUESTION_STATUS:
            return {
                ...state,
                isLogic: false,
                builderState: BUILDER_STATES.EDIT,
                isNewQuestion: false,
                currentQuestion: action.payload.currentQuestion
                // isNewQuestion: true,
                // currentQuestion: { type: QTYPES.VIRTUAL, parent: 0 }
            };
        case actionType.CLEAR_STATE:
            return { ...initialState };

        default:
            return state;
    }
}
